import {
  AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChildren, inject
} from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import {
  Validators, ReactiveFormsModule, FormGroupDirective, ControlContainer, FormGroup, FormControl,
  ValidatorFn, ValidationErrors, AbstractControl, NonNullableFormBuilder
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ValidationMessageComponent } from '../validation-message/validation-message.component';
import {
  BehaviorSubject, combineLatest, combineLatestWith, distinctUntilChanged, map, skip, startWith,
  Subject, take, takeUntil
} from 'rxjs';
import { TypedForm } from '@modules/shared/interfaces/typed-form/typed-form';
import { TextareaWithChipsComponent } from '../textarea-with-chips/textarea-with-chips.component';
import { FormSelectWithErrorsComponent } from '@modules/shared/components/form-select-with-errors/form-select-with-errors.component';
import { InformationTemplate, TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { FormSelectOption } from '../form-select/interfaces/form-select-option';
import { TrafficDeviationForm } from '@components/traffic-deviations/interfaces/traffic-deviation-form';
import { TrafficDeviationService } from '@components/traffic-deviations/services/traffic-deviation.service';
import { TrafficDeviationInformationAdvancedOptions } from '@modules/shared/models/traffic-deviation-information-advanced-options';
import { InformationDetailsComponent } from '../information-details/information-details.component';
import { FormDateComponent } from '@modules/shared/components/form-date/form-date.component';
import { FormTimeComponent } from '@modules/shared/components/form-time/form-time.component';
import { FormSwitchComponent } from '../form-switch/form-switch.component';
import _ from 'lodash';

interface DeviationTemplateOption extends FormSelectOption {
  templateId: string;
  version: number;
};

@Component({
    selector: 'app-create-traffic-deviations-deviation-information',
    imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ValidationMessageComponent,
    AsyncPipe,
    TextareaWithChipsComponent,
    FormSelectWithErrorsComponent,
    InformationDetailsComponent,
    FormDateComponent,
    FormTimeComponent,
    FormSwitchComponent
],
    templateUrl: './deviation-information.component.html',
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    styleUrls: ['./deviation-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviationInformationComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _formGroupDirective = inject(FormGroupDirective);
  private readonly _deviationStore = inject(TrafficDeviationsStore);
  private readonly _deviationService = inject(TrafficDeviationService);
  private readonly _formBuilder = inject(NonNullableFormBuilder);

  @ViewChildren(TextareaWithChipsComponent) textareaWithChipsComponent: TextareaWithChipsComponent[];

  public clearFields = false;
  public readonly togglePublicationPeriod = new FormControl(false);
  public readonly triggerHandleTemplate$ = new BehaviorSubject<void>(undefined);
  public readonly requiredCustomControls$ = new BehaviorSubject<string[]>([]);

  protected deviationReasons = [];

  protected selectActive = false;

  protected trafficDeviationInformationGroup: FormGroup<TypedForm<TrafficDeviationForm['trafficDeviationInformation']>>;

  protected readonly selectedTemplate$ = new BehaviorSubject<InformationTemplate>(undefined);

  protected readonly templateOptions = new FormControl('')

  protected readonly templateOptions$: BehaviorSubject<ReadonlyArray<DeviationTemplateOption>> = new BehaviorSubject([]);

  protected readonly advancedOptions$: BehaviorSubject<ReadonlyArray<FormSelectOption>> = new BehaviorSubject(
    [{ value: '', text: 'No special publication', selected: true }, { value: TrafficDeviationInformationAdvancedOptions.BOARD_ONLY, text: 'Show only on collection signs' },
    { value: TrafficDeviationInformationAdvancedOptions.STOP_INDEPENDENT, text: 'Also show to passersby' }, { value: TrafficDeviationInformationAdvancedOptions.EXCLUSIVE, text: 'Crisis mode' }]
  );

  protected get getterRouteInfo() {
 return this._parentForm.controls.affected.controls.routeInfo; 
}

  protected get getterDisplayScope() {
    return this.trafficDeviationInformationGroup.controls.displayScope.controls;
  }

  protected get isAtLeastOneRequiredValid(): boolean {
    return !this.trafficDeviationInformationGroup.controls.displayScope.hasError('atLeastOneRequired');
  }

  protected isPublicationFieldsSet(): boolean {
    const c = this.trafficDeviationInformationGroup.controls;
    return !!(c.publicationStartDate.value && c.publicationStartTime.value
      && c.publicationEndDate.value && c.publicationEndTime.value);
  }

  protected _parentForm: FormGroup<TypedForm<TrafficDeviationForm>>;
  private _tempCustomProperties: { [x: string]: string | number | boolean; }[] = [];
  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this._parentForm = this._formGroupDirective.form;
    this.trafficDeviationInformationGroup = this._parentForm.controls.trafficDeviationInformation;

    this._init();

    this.handleTemplates();

    this.togglePublicationPeriod.valueChanges.pipe(
      takeUntil(this._destroy$)
    ).subscribe((togglePublicationPeriod) => {

      const informationGroup = this.trafficDeviationInformationGroup.controls;
      const affectedControls = this._parentForm.controls.affected.controls;

      if (togglePublicationPeriod) {
        informationGroup.publicationStartDate.addValidators([Validators.required]);
        informationGroup.publicationEndDate.addValidators([Validators.required]);
        informationGroup.publicationStartTime.addValidators([Validators.required]);
        informationGroup.publicationEndTime.addValidators([Validators.required]);

        if (!informationGroup.publicationStartDate.value) {
          const startTime = new Date();
          startTime.setSeconds(0);

          this.trafficDeviationInformationGroup.patchValue({
            publicationStartDate: startTime.toLocaleDateString('sv-SE'),
            publicationStartTime: startTime.toLocaleTimeString('sv-SE'),
          });
        }

        this.trafficDeviationInformationGroup.patchValue({
          publicationEndDate: affectedControls.endDate.value,
          publicationEndTime: affectedControls.endTime.value
        });
      }

      if (!togglePublicationPeriod) {
        informationGroup.publicationStartDate.removeValidators([Validators.required]);
        informationGroup.publicationEndDate.removeValidators([Validators.required]);
        informationGroup.publicationStartTime.removeValidators([Validators.required]);
        informationGroup.publicationEndTime.removeValidators([Validators.required]);
        this.trafficDeviationInformationGroup.patchValue({
          publicationEndDate: '',
          publicationStartDate: '',
          publicationStartTime: '',
          publicationEndTime: '',
        });
      }
      this.trafficDeviationInformationGroup.updateValueAndValidity();
    });

    const affectedControls = this._parentForm.controls.affected.controls;
    affectedControls.endDate.valueChanges.pipe(
      takeUntil(this._destroy$)
    ).subscribe((endDate) => {
      if (this.togglePublicationPeriod.value) {
        this.trafficDeviationInformationGroup.patchValue({ publicationEndDate: endDate });
      }
    });

    affectedControls.endTime.valueChanges.pipe(
      takeUntil(this._destroy$)
    ).subscribe((endTime) => {
      if (this.togglePublicationPeriod.value) {
        this.trafficDeviationInformationGroup.patchValue({ publicationEndTime: endTime });
      }
    });

    this.getterDisplayScope.advancedOptions.valueChanges.pipe(
      startWith(this.getterDisplayScope.advancedOptions.value),
      takeUntil(this._destroy$)
    ).subscribe((value) => {
      if (value === TrafficDeviationInformationAdvancedOptions.BOARD_ONLY ||
        value === TrafficDeviationInformationAdvancedOptions.EXCLUSIVE) {
        this.getterDisplayScope.signDisplay.setValue(true);
        this.getterDisplayScope.signDisplay.disable();
        this.getterDisplayScope.journeyPlanner.enable();
      } else if (value === TrafficDeviationInformationAdvancedOptions.STOP_INDEPENDENT) {
        this.getterDisplayScope.journeyPlanner.setValue(true);
        this.getterDisplayScope.journeyPlanner.disable();
        this.getterDisplayScope.signDisplay.enable();
      } else {
        this.getterDisplayScope.signDisplay.enable();
        this.getterDisplayScope.journeyPlanner.enable();
      }

      this.trafficDeviationInformationGroup.controls.displayScope.addValidators(this.atLeastOneSelectedValidator());
    });
  }

  ngAfterViewInit(): void {
    this._handleAllChipsHasValue();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.getterRouteInfo.controls.forEach((routeInfo) => {
      routeInfo.controls.calls.clearValidators();
      routeInfo.controls.calls.setErrors(null);
      routeInfo.controls.calls.updateValueAndValidity();
    });
  }

  public getTextareaWithChips(textareaId: string): string {
    const textareaComponent = this.textareaWithChipsComponent.find((t) => t.options.id === textareaId);
    return textareaComponent?.getTextWithoutChips();
  }

  public reInitialize(): void {
    this._init();
    this.triggerHandleTemplate$.next();
  }

  /**
   * Handles changes in which templates to show on changed deviation type,
   * and makes sure to set the form on selected template
   */
  protected handleTemplates(): void {
    combineLatest([
      this._deviationStore.informationTemplates$,
      this._deviationService.selectedDeviationType$,
      this.triggerHandleTemplate$
    ]).pipe(
      map(([templates, type]) => templates.filter((t) => t.deviationTypes.includes(type))),
      takeUntil(this._destroy$),
    ).subscribe((templates) => {
      const information = this.trafficDeviationInformationGroup.value;
      let templateOptions: Array<DeviationTemplateOption> = templates.map((t) => ({
        templateId: t.id,
        version: t.version,
        value: t.name,
        text: t.name,
        selected: t.defaultTemplate
      }));

      templateOptions = [{
        templateId: '',
        version: 0,
        value: '',
        text: 'Choose template',
        selected: false
      }, ...templateOptions];

      let selectedOption = templateOptions.find((t) => t.selected);
      if (!selectedOption) {
        selectedOption = templateOptions[0];
      }
      if (information.templateId) {
        const newSelectedOption = templateOptions.find((t) => {
          return t.templateId === information.templateId && t.version === information.version;
        });
        selectedOption = newSelectedOption || selectedOption;
      }

      this.templateOptions$.next(templateOptions);
      this.templateOptions.setValue(<string>selectedOption.value);
    });

    this.templateOptions$.pipe(
      distinctUntilChanged(),
      combineLatestWith(this._deviationStore.informationTemplates$),
      map(([templateOptions, templates]) => {
        const templateOption = templateOptions.filter((t) => t.selected)[0];
        let template = templates.find((t) => t.id === templateOption?.templateId);
        return template;
      }),
      takeUntil(this._destroy$)
    ).subscribe((template) => {
      this.selectedTemplate$.next(template);
    });

    this.selectedTemplate$.pipe(
      skip(1),
      distinctUntilChanged(),
      takeUntil(this._destroy$)
    ).subscribe((template) => {
      this._setRequiredCustomControls(template);

      if (template) {
        const informationForm = this.trafficDeviationInformationGroup.value;
        if (
          informationForm.templateId === template.id
          && informationForm.version !== template.version
        ) {
          return;
        }

        this.trafficDeviationInformationGroup.patchValue({
          templateId: template.id,
          version: template.version,
          summary: template.summary,
          header: template.header,
          content: template.content
        });
      } else if (this.clearFields) {
        this.trafficDeviationInformationGroup.patchValue({
          templateId: '',
          version: 0,
          summary: '',
          header: '',
          content: '',
          allChipsHasValue: true,
          displayScope: { advancedOptions: '', journeyPlanner: true, signDisplay: true },
          publicationEndDate: '',
          publicationEndTime: '',
          publicationStartDate: '',
          publicationStartTime: ''
        });
      }

      this.clearFields = true;
    });
  }

  protected onKeydownPicker(event: KeyboardEvent, elementRef: HTMLInputElement): void {
    if (event.code === "Enter" || event.code === "Space") {
      elementRef.showPicker();
    }
  }

  private _setRequiredCustomControls(template: InformationTemplate): Promise<void> {
    this.requiredCustomControls$.next([]);
    if (!template) {
      return;
    }

    let requiredCustomControls: string[] = [];
    let manualVariables: string[] = [];
    let variablesToExclude: string[] = [];

    combineLatest([
      this._deviationStore.automaticVariables$,
      this._deviationStore.automaticRouteInfoVariable$,
      this._deviationStore.manualVariables$
    ]).pipe(
      take(1)
    ).subscribe(([automatic, automaticRouteInfo, manual]) => {
      variablesToExclude = [...automatic, ...automaticRouteInfo];
      manualVariables = [...manual];
    });

    const hashVariables = new Set<string>();
    const findHashVariables = (text: string, regex: RegExp): void => {
      if (!text) return;
      Array.from(text.matchAll(regex), match => match[1])?.forEach(value => {
        hashVariables.add(value);
      });
    };

    let regex = /#([^#]+)#/g;
    findHashVariables(template.content, regex);
    findHashVariables(template.header, regex);
    findHashVariables(template.summary, regex);

    regex = /<span[^>]*>(.*?)<\/span>/g;
    findHashVariables(template.content, regex);
    findHashVariables(template.header, regex);
    findHashVariables(template.summary, regex);

    let usedVariables = Array.from(hashVariables);
    usedVariables = usedVariables.filter((variable) => {
      return !variablesToExclude.some((automaticVariable) => {
        return variable === automaticVariable;
      });
    });

    if (!usedVariables?.length) return;

    requiredCustomControls = [
      ...new Set([...requiredCustomControls, ...usedVariables])
    ];
    this.requiredCustomControls$.next(requiredCustomControls);

    const custom = this.trafficDeviationInformationGroup.controls.custom;
    if (requiredCustomControls?.length) {
      const customGroup = this._formBuilder.group({});
      requiredCustomControls.forEach((name) => {
        if (manualVariables?.includes(name)) {
          const value = this._tempCustomProperties[0]?.[name] || '';
          custom.at(0)?.get(name)?.setValue(value);
          return;
        }

        const value = this._tempCustomProperties[1]?.[name] || '';
        customGroup.addControl(name, this._formBuilder.control(value, [Validators.required]));
      });

      if (Object.keys(customGroup.controls).length) {
        const wasGroupTouched = custom.length > 1 && custom.at(1).touched;
        if (wasGroupTouched) {
          customGroup.markAllAsTouched();
        }
        if (custom.length > 1) {
          custom.setControl(1, customGroup);
        } else {
          custom.push(customGroup);
        }
      }
    }
  }

  private atLeastOneSelectedValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const signDisplay = formGroup.get('signDisplay')?.value;
      const journeyPlanner = formGroup.get('journeyPlanner')?.value;

      if (!signDisplay && !journeyPlanner) {
        return { atLeastOneRequired: true };
      }

      return null;
    };
  }

  private _handleAllChipsHasValue(): void {
    const allChipsHasValue = this.textareaWithChipsComponent?.map((textarea) => {
      return textarea.allChipsHasValue$.asObservable()
    });

    if (!allChipsHasValue?.length) return;

    combineLatest(allChipsHasValue).pipe(
      map((values) => !values.some((obj) => Object.values(obj).some(value => value === false))),
      takeUntil(this._destroy$)
    ).subscribe((allChipsHasValue) => {
      this.trafficDeviationInformationGroup.patchValue({ allChipsHasValue });
    });
  }

  private _clearCustomProperties(): void {
    const custom = this.trafficDeviationInformationGroup.controls.custom;
    this._tempCustomProperties = structuredClone(custom.getRawValue());
    custom.removeAt(1);

    if (!custom.controls.at(0)) return;

    for (const control of Object.values(custom.controls.at(0).controls)) {
      control.setValue('');
      control.updateValueAndValidity();
    }
  }

  private _init(): void {
    this._clearCustomProperties();
    this.clearFields = false;
    if (this.trafficDeviationInformationGroup.value.publicationStartDate) {
      this.togglePublicationPeriod.setValue(true);
    }
  }
}
